import { ActionType, CompletePricePointsStepPayload } from "../../AppDataProvider/actions";
import { AppContext, BookingData, PricePoint, SelectedPricePoint } from "../../AppDataProvider/AppDataProvider";
import { Divider as BaseDivider, Input, List, ListItem, NavButtons, Notification, Pill } from "../../common/components";
import { getCurrencyDisplay, requireBookingDataFields } from "../../utils";
import { marginBottom, marginRight, marginTop, marginY, media, paddingRight, typography } from "../../common/styles";
import React, { ReactElement, useContext, useState } from "react";
import { applyOpacity } from "../../common/styles/utils";
import { PartRequired } from "../../utils/types";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
type PricePointsStepBookingData = PartRequired<BookingData, "organisation" | "service" | "tier">;
export function PricePointsStep(): ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  requireBookingDataFields<PricePointsStepBookingData>(appData.bookingData, ["organisation", "service", "tier"]);
  const {
    organisation,
    service,
    tier,
    pricePoints
  } = appData.bookingData;

  /**
   * This is falling back to 500 because the value is currently incorrectly
   * marked as optional on the back-end but is a required field.
   *
   * It defaults to 500 in the rest of the Appointedd booking tools so
   * falling back to 500 for now until time is taken to make this a mandatory field.
   */
  const maxParticipantsPerBooking = service.maxParticipantsPerBooking ?? 500;
  const [selectedPricePoints, setSelectedPricePoints] = useState<SelectedPricePoint[]>(pricePoints ?? []);
  const [errorVisible, setErrorVisible] = useState(false);
  const getPriceDisplay = ({
    price,
    showZero
  }: {
    price: number;
    showZero?: boolean;
  }): string => {
    return price === 0 && !showZero ? t("free") : getCurrencyDisplay(price, organisation.currency, i18n.language);
  };
  const getTotalPrice = (): number => {
    return selectedPricePoints.reduce((accumulator, pricePoint) => accumulator + pricePoint.pricePoint.pricePerQuantity * pricePoint.quantity, 0);
  };
  const getTotalAttendees = (pricePoints: SelectedPricePoint[]): number => {
    return pricePoints.reduce((accumulator, pricePoint) => accumulator + pricePoint.pricePoint.spacesPerQuantity * pricePoint.quantity, 0);
  };

  /**
   * Get the maximum quantity that the provided price point
   * can increment to factoring in the quantity of the
   * other price points that are already selected.
   */
  const getInputMaxQuantity = (pricePoint: PricePoint) => {
    const currentPricePoint = selectedPricePoints.filter(selectedPricePoint => selectedPricePoint.pricePoint._id === pricePoint._id);
    const totalAttendees = getTotalAttendees(selectedPricePoints);
    const remainingSpaces = maxParticipantsPerBooking - totalAttendees;
    const remainingQuantity = Math.floor(remainingSpaces / pricePoint.spacesPerQuantity);
    const existingQuantity = currentPricePoint[0] ? currentPricePoint[0].quantity : 0;
    const maxQuantity = existingQuantity + remainingQuantity;
    return maxQuantity;
  };
  const onAttendeesChange = (pricePoint: PricePoint, quantity: number | undefined) => {
    const selectedPricePointIds = selectedPricePoints.map(selectedPricePoint => selectedPricePoint.pricePoint._id);
    if (selectedPricePointIds.includes(pricePoint._id)) {
      const newSelectedPricePoints = [...selectedPricePoints];
      const currentPricePoint = newSelectedPricePoints.find(selectedPricePoint => selectedPricePoint.pricePoint._id === pricePoint._id);
      if (quantity === 0) {
        const currentPricePointIndex = newSelectedPricePoints.findIndex(selectedPricePoint => selectedPricePoint.pricePoint._id === currentPricePoint?.pricePoint._id);
        newSelectedPricePoints.splice(currentPricePointIndex, 1);
      } else {
        currentPricePoint && quantity && (currentPricePoint.quantity = quantity);
      }
      setSelectedPricePoints(newSelectedPricePoints);
    } else {
      quantity && quantity > 0 && setSelectedPricePoints([...selectedPricePoints, {
        pricePoint,
        quantity
      }]);
      if (errorVisible) setErrorVisible(false);
    }
  };
  const completeStep = () => {
    const payload: CompletePricePointsStepPayload = {
      updatedData: {
        pricePoints: selectedPricePoints
      }
    };
    selectedPricePoints.length ? appDataDispatch({
      payload,
      type: ActionType.COMPLETE_PRICE_POINTS_STEP
    }) : setErrorVisible(true);
  };
  const goBack = () => {
    appDataDispatch({
      type: ActionType.BACK_FROM_PRICE_POINTS_STEP
    });
  };

  /**
   * If we come back to this step we want to make sure
   * the user retains their already selected price points
   */
  const getQuantity = (pricePointId: string): number => {
    const existingPricePoint = pricePoints?.find(pricePoint => pricePoint.pricePoint._id === pricePointId);
    return existingPricePoint ? existingPricePoint.quantity : 0;
  };
  return <>
      <List data-sentry-element="List" data-sentry-source-file="PricePointsStep.tsx">
        {tier.pricePoints.map(pricePoint => <ListItem key={pricePoint._id}>
            <PricePointInfo>
              <Name>{pricePoint.name}</Name>
              <PriceAndAttendees>
                <Attendees>
                  {t("attendeeCount", {
                count: pricePoint.spacesPerQuantity
              })}
                </Attendees>
                <Price>
                  {getPriceDisplay({
                price: pricePoint.pricePerQuantity
              })}
                </Price>
              </PriceAndAttendees>
            </PricePointInfo>
            <InputContainer>
              <Input variant="number" value={getQuantity(pricePoint._id)} id={pricePoint._id} aria-label={t("selectQuantity")} max={getInputMaxQuantity(pricePoint)} min={0} onValueChange={quantity => onAttendeesChange(pricePoint, quantity)} />
            </InputContainer>
          </ListItem>)}
      </List>

      <Divider data-sentry-element="Divider" data-sentry-source-file="PricePointsStep.tsx" />

      <LineItem data-sentry-element="LineItem" data-sentry-source-file="PricePointsStep.tsx">
        <Label data-sentry-element="Label" data-sentry-source-file="PricePointsStep.tsx">{t("attendee_other")}</Label>
        <TotalAttendees aria-label={t("attendee_other")} data-sentry-element="TotalAttendees" data-sentry-source-file="PricePointsStep.tsx">
          {getTotalAttendees(selectedPricePoints)}
        </TotalAttendees>
      </LineItem>

      <LineItem data-sentry-element="LineItem" data-sentry-source-file="PricePointsStep.tsx">
        <Label data-sentry-element="Label" data-sentry-source-file="PricePointsStep.tsx">{t("total")}</Label>
        <Pill aria-label={t("total")} data-sentry-element="Pill" data-sentry-source-file="PricePointsStep.tsx">
          {getPriceDisplay({
          price: getTotalPrice(),
          showZero: true
        })}
        </Pill>
      </LineItem>

      {errorVisible && <ErrorNotification variant="warning">
          {t("errors.pricePointRequired")}
        </ErrorNotification>}
      <NavButtons {...{
      onBack: goBack,
      onContinue: completeStep
    }} data-sentry-element="NavButtons" data-sentry-source-file="PricePointsStep.tsx" />
    </>;
}
const PricePointInfo = styled.div`
  display: flex;
  flex-direction: column;
`;
const Name = styled.h2`
  ${typography.subtitle}
  ${marginTop.zero}
  ${marginBottom.s}
`;
const PriceAndAttendees = styled.div`
  display: flex;
  flex-direction: row;
  @media ${media.mobile} {
    justify-content: space-between;
  }
`;
const Attendees = styled.div`
  ${marginRight.s}
  ${typography.small};
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
`;
const Price = styled.span`
  ${typography.smallBold}
`;
const InputContainer = styled.div`
  flex-shrink: 0;
  justify-self: flex-end;
  @media ${media.mobile} {
    align-self: flex-end;
    ${marginTop.s}
  }
`;
const Divider = styled(BaseDivider)`
  display: none;
  @media ${media.mobile} {
    ${marginY.m}
    display: block;
  }
`;
const LineItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:nth-child(3) {
    ${marginTop.m}
    @media ${media.mobile} {
      ${marginTop.zero}
    }
  }
  &:not(:last-child) {
    ${marginBottom.s}
  }
`;
const Label = styled.div`
  text-transform: capitalize;
  ${typography.body};
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
`;
const TotalAttendees = styled.div`
  ${paddingRight.m}
  ${typography.bodyBold}
`;
const ErrorNotification = styled(Notification)`
  ${marginTop.m}
`;